import { LuIcon, Menu, type MenuRootProps, type MenuVariantProps } from "@overmap-ai/blocks"
import type { ProcedureInitiative, ProcedureStep, Stored, User } from "@overmap-ai/common"
import { ConditionManager, type Field, FieldSchema } from "@overmap-ai/forms"
import { Eye } from "lucide-react"
import { Fragment, memo, type ReactElement, useCallback, useMemo } from "react"

import { CreatedByFilterDef } from "../../filtering/defs/CreatedByFilterDef"
import { ProcedureInitiativeFilterDef } from "../../filtering/defs/ProcedureInitiativeFilterDef"
import { ProcedureStepAssigneeFilterDef } from "../../filtering/defs/ProcedureStepAssigneeFilterDef"
import { ProcedureStepCompletionStatusFilterDef } from "../../filtering/defs/ProcedureStepCompletionStatusFilterDef"
import { ProcedureStepReviewerFilterDef } from "../../filtering/defs/ProcedureStepReviewerFilterDef"
import { ProcedureStepReviewStatusFilterDef } from "../../filtering/defs/ProcedureStepReviewStatusFilterDef"
import { SubmittedAtFilterDef } from "../../filtering/defs/SubmittedAtFilterDef"
import { getFilterDefOfField } from "../../filtering/defs/utils"
import { FilterManager } from "../../filtering/manager"
import { modelIconMapping } from "../../mappings/icons"
import type { ProcedureData } from "../../typings"
import { FieldGroup } from "../MenuGroups/FieldGroup"

export interface ProcedureDataFilterMenuProps extends MenuRootProps, MenuVariantProps {
	children: ReactElement
	filterManager: FilterManager<ProcedureData>
	procedureInitiatives: Stored<ProcedureInitiative>[]
	procedureTypeFieldSchema: FieldSchema
	procedureSteps: Stored<ProcedureStep>[]
	procedureStepFieldSchemasAndConditions: Record<ProcedureStep["uuid"], [FieldSchema, ConditionManager]>
	users: User[]
}

export const ProcedureDataFilterMenu = memo<ProcedureDataFilterMenuProps>((props) => {
	const {
		children,
		variant,
		size,
		accentColor,
		filterManager,
		procedureInitiatives,
		procedureTypeFieldSchema,
		procedureSteps,
		procedureStepFieldSchemasAndConditions,
		users,
		...rest
	} = props

	const handleSelectCreatedBy = useCallback(() => {
		filterManager.addFilter(new CreatedByFilterDef(users).createFilter())
	}, [filterManager, users])

	const handleSelectSubmittedAt = useCallback(() => {
		filterManager.addFilter(new SubmittedAtFilterDef().createFilter())
	}, [filterManager])

	const handleSelectProcedureInitiative = useCallback(() => {
		filterManager.addFilter(new ProcedureInitiativeFilterDef(procedureInitiatives).createFilter())
	}, [filterManager, procedureInitiatives])

	const handleSelectProcedureStepCompletionStatus = useCallback(
		(procedureStep: Stored<ProcedureStep>) => {
			filterManager.addFilter(new ProcedureStepCompletionStatusFilterDef(procedureStep).createFilter())
		},
		[filterManager],
	)

	const handleSelectProcedureStepReviewStatus = useCallback(
		(procedureStep: Stored<ProcedureStep>) => {
			filterManager.addFilter(new ProcedureStepReviewStatusFilterDef(procedureStep).createFilter())
		},
		[filterManager],
	)

	const handleSelectProcedureStepAssignees = useCallback(
		(procedureStep: Stored<ProcedureStep>) => {
			filterManager.addFilter(new ProcedureStepAssigneeFilterDef(procedureStep, users).createFilter())
		},
		[filterManager, users],
	)

	const handleSelectProcedureStepReviewers = useCallback(
		(procedureStep: Stored<ProcedureStep>) => {
			filterManager.addFilter(new ProcedureStepReviewerFilterDef(procedureStep, users).createFilter())
		},
		[filterManager, users],
	)

	const handleFieldSelect = useCallback(
		(field: Field) => {
			filterManager.addFilter(getFilterDefOfField(field).createFilter())
		},
		[filterManager],
	)

	const supportedProcedureTypeFields = useMemo(() => procedureTypeFieldSchema.getFields(), [procedureTypeFieldSchema])

	const supportedProcedureStepFields = useMemo(() => {
		if (!procedureStepFieldSchemasAndConditions) return

		const ret: Record<ProcedureStep["uuid"], Field[]> = {}

		for (const [uuid, [fields]] of Object.entries(procedureStepFieldSchemasAndConditions)) {
			ret[uuid] = fields.getFields()
		}

		return ret
	}, [procedureStepFieldSchemasAndConditions])

	return (
		<Menu.Root {...rest}>
			<Menu.ClickTrigger>{children}</Menu.ClickTrigger>
			<Menu.Content variant={variant} size={size} accentColor={accentColor}>
				<Menu.Scroll>
					<Menu.Item onSelect={handleSelectCreatedBy}>
						<LuIcon icon={modelIconMapping.creator} />
						Created by
					</Menu.Item>
					<Menu.Item onSelect={handleSelectSubmittedAt}>
						<LuIcon icon={modelIconMapping.dueDate} />
						Submitted at
					</Menu.Item>
					<Menu.Item onSelect={handleSelectProcedureInitiative}>
						<LuIcon icon={modelIconMapping.procedureInitiave} />
						Initiative
					</Menu.Item>
					{!!supportedProcedureTypeFields && supportedProcedureTypeFields.length > 0 && (
						<>
							<Menu.Label>Procedure fields</Menu.Label>
							<FieldGroup fields={supportedProcedureTypeFields} onFieldSelect={handleFieldSelect} />
						</>
					)}

					{!!procedureSteps &&
						!!supportedProcedureStepFields &&
						procedureSteps.length > 0 &&
						procedureSteps.map((procedureStep) => {
							const fields = supportedProcedureStepFields[procedureStep.uuid]

							return (
								<Fragment key={procedureStep.uuid}>
									<Menu.Label>{procedureStep.name}</Menu.Label>
									<Menu.Item
										onSelect={() => handleSelectProcedureStepCompletionStatus(procedureStep)}
									>
										<LuIcon icon={modelIconMapping.status} />
										Completion
									</Menu.Item>
									<Menu.Item onSelect={() => handleSelectProcedureStepReviewStatus(procedureStep)}>
										<LuIcon icon={Eye} />
										Review
									</Menu.Item>
									<Menu.Item onSelect={() => handleSelectProcedureStepAssignees(procedureStep)}>
										<LuIcon icon={modelIconMapping.assignee} />
										Assignees
									</Menu.Item>
									<Menu.Item onSelect={() => handleSelectProcedureStepReviewers(procedureStep)}>
										<LuIcon icon={modelIconMapping.reviewer} />
										Reviewers
									</Menu.Item>
									{fields.length > 0 && (
										<FieldGroup fields={fields} onFieldSelect={handleFieldSelect} />
									)}
								</Fragment>
							)
						})}
				</Menu.Scroll>
			</Menu.Content>
		</Menu.Root>
	)
})
ProcedureDataFilterMenu.displayName = "ProcedureDataFilterMenu"
